/**
 * Svelte action implementing an accessible focus trap for modal dialogs.
 *
 * On mount it remembers the currently focused element and moves focus inside `node`.
 * While active, Tab / Shift+Tab are kept cycling within the focusable descendants of `node`.
 * On destroy, focus is restored to the element that was focused before the trap was activated.
 */
const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "iframe",
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function focusTrap(node: HTMLElement) {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    function getFocusable(): HTMLElement[] {
        return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
            (el) => el.offsetParent !== null || el === document.activeElement
        );
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key !== "Tab") {
            return;
        }
        const focusable = getFocusable();
        if (focusable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey) {
            if (active === first || !node.contains(active)) {
                event.preventDefault();
                last.focus();
            }
        } else if (active === last || !node.contains(active)) {
            event.preventDefault();
            first.focus();
        }
    }

    // Move focus inside the trap on activation.
    const focusable = getFocusable();
    (focusable[0] ?? node).focus();

    node.addEventListener("keydown", handleKeydown);

    return {
        destroy() {
            node.removeEventListener("keydown", handleKeydown);
            if (previouslyFocused && typeof previouslyFocused.focus === "function") {
                previouslyFocused.focus();
            }
        },
    };
}
